import React, { useEffect, useState } from 'react';

import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, redirect } from '@tanstack/react-router';

import { Box, Tab, Tabs } from '@mui/material';

import { claimQueries } from '@repo/api/claim/claims';
import {
	useApproveDocument,
	useDocumentDelete,
	useDocumentSubmit,
} from '@repo/api/claim/documents';
import { questionnaireQueries } from '@repo/api/claim/questionnaire';
import { useUser } from '@repo/auth';
import { DocumentsPage, Sales } from '@repo/features/documents';

import { ErrorMessage } from './-components/ErrorMessage';
import { Route as BusinessRoute } from './business';

const documentTabs = ['financialStatement', 'sales'] as const;
type DocumentTab = (typeof documentTabs)[number];

export const Route = createFileRoute(
	'/_claims/claim/$claimId/_claim/documents',
)({
	beforeLoad: ({ params, context }) => {
		if (!context.user?.permissions.includes('claim.document.view')) {
			throw redirect({
				to: BusinessRoute.to,
				params,
			});
		}
	},
	component: function DocumentsTab() {
		const params = Route.useParams();

		const navigate = Route.useNavigate();

		const claimId = params.claimId;

		const [defaultComparisonDate] = useState(() => new Date().toISOString());

		const { permissions } = useUser();

		const { data: claim, isFetching } = useSuspenseQuery(
			claimQueries.id(claimId),
		);

		const { data: questionnaire } = useSuspenseQuery(
			questionnaireQueries.id(claimId),
		);

		const [activeTab, setActiveTab] =
			useState<DocumentTab>('financialStatement');

		useEffect(() => {
			if (!isFetching && !claim.questionnaireCompleted) {
				void navigate({
					to: BusinessRoute.to,
				});
			}
		}, [claim.questionnaireCompleted, isFetching, navigate]);

		const { mutateAsync: deleteDocument } = useDocumentDelete(claim.claimId);
		const { mutateAsync: approveDocument } = useApproveDocument(claim.claimId);

		const { mutateAsync: submitDocument, isPending: submitting } =
			useDocumentSubmit(claim.claimId);

		if (!claim.stage) {
			return <ErrorMessage message="Claim not found" />;
		}

		const tabTitles: Record<DocumentTab, string> = {
			sales: 'Sales',
			financialStatement: 'Financial Statement',
		};

		const tabComponents: Record<DocumentTab, () => React.ReactNode> = {
			sales: () => <Sales claimId={claimId} />,
			financialStatement: () =>
				claim.stage ?
					<DocumentsPage
						title="Claim Documents"
						documents={claim.claimDocuments}
						stage={claim.stage}
						showFileProgress
						deleteDocument={deleteDocument}
						approveDocument={approveDocument}
						permissions={{
							download: permissions.includes('claim.document.download'),
							approve: permissions.includes('claim.document.approve'),
							deny: permissions.includes('claim.document.deny'),
							close: permissions.includes('claim.close'),
						}}
						canUpload={permissions.includes('claim.document.upload')}
						countryId={claim.claimInsured.countryId}
						comparisonDate={claim.dateofLoss || defaultComparisonDate}
						submitDocument={submitDocument}
						submitting={submitting}
						introAcknowledgement="completing the business questionnaire"
						uploadTarget="your claim"
					/>
				:	null,
		};

		if (claim.isTier2Enabled && questionnaire.isTier2) {
			return (
				<Box sx={{ width: '100%' }}>
					<Tabs
						value={activeTab}
						onChange={(_, newValue) => {
							setActiveTab(newValue as DocumentTab);
						}}
						sx={{ mb: 4 }}
					>
						{documentTabs.map((tab) => (
							<Tab key={tab} value={tab} label={tabTitles[tab]} />
						))}
					</Tabs>
					{tabComponents[activeTab]()}
				</Box>
			);
		}

		return tabComponents.financialStatement();
	},
	head: (ctx) => ctx.match.context.setTitle('Claim', 'Documents'),
});
